// @ts-strict-ignore
import { Ref, memo, forwardRef, ComponentProps, useMemo } from 'react'
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers'
import { FormValueInput, FormValueMeta } from 'types'
import { DateLike, ensureDateAsString, ensureMoment, formatAsDate } from 'services/formatting'
import TextField from '../TextField'

export interface IDatePickerProps extends Omit<ComponentProps<typeof MUIDatePicker>, 'value' | 'onChange' | 'renderInput'> {
  value?: DateLike
  onChange?: (value: DateLike) => any
  input?: FormValueInput<DateLike>
  meta?: FormValueMeta
  helperText?: string
  fullWidth?: boolean
  'data-cy'?: string
}

function DatePicker(props: IDatePickerProps, ref: Ref<HTMLInputElement>) {
  const { input, meta = {} as FormValueMeta, value, onChange, fullWidth = true, helperText, ...restProps } = props

  const error = meta.touched ? meta.error : undefined
  const actualHelperText = error ?? helperText as string
  const actualValue = useMemo(() => ensureMoment(input ? input.value : value), [input?.value, value])
  const actualValueFormatted = useMemo(() => {
    return formatAsDate(actualValue) as string
  }, [actualValue])
  const actualOnChange = val => input ? input.onChange(ensureDateAsString(val)) : onChange(ensureDateAsString(val))

  return (
    <MUIDatePicker
      {...restProps}
      renderInput={({ value: inputValue, inputProps, ...params }) => {
        return (
          <TextField
            {...params}
            ref={params.inputRef}
            value={actualValueFormatted}
            helperText={actualHelperText}
            fullWidth={fullWidth}
            error={!!error}
            inputProps={{
              ...inputProps,
              'data-cy': restProps['data-cy'],
            }}
          />
        )
      }}
      inputRef={ref}
      onChange={actualOnChange}
      value={actualValue || null}
    />
  )
}

export default memo(forwardRef(DatePicker))
